import {
    FloatingFocusManager,
    FloatingOverlay,
    FloatingPortal,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useMergeRefs,
    useRole,
} from "@floating-ui/react";

import {StateUpdater, useContext, useMemo, useState} from "preact/hooks";
import {ComponentChildren, createContext} from "preact";
import {forwardRef, HTMLProps, Ref} from "react";
import styled from "styled-components";
import {Button, ButtonProps} from "./button";

export interface DialogOptions {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    closeOnClickOutside?: boolean;
    resolve?: (reason: string) => void;
}

export function useDialog({open, onOpenChange, closeOnClickOutside, resolve}: DialogOptions) {
    const [labelId, setLabelId] = useState<string | undefined>();
    const [descriptionId, setDescriptionId] = useState<string | undefined>();

    const data = useFloating({
        open,
        onOpenChange: onOpenChange
    });

    const context = data.context;

    const click = useClick(context, {enabled: open == null});
    const dismiss = useDismiss(context, { outsidePressEvent: "mousedown", enabled: closeOnClickOutside ?? false });
    const role = useRole(context);
    const interactions = useInteractions([click, dismiss, role]);

    return useMemo(() => ({
        open,
        setOpen: onOpenChange,
        ...interactions,
        ...data,
        labelId,
        descriptionId,
        setLabelId,
        setDescriptionId,
        resolve,
    }), [open, onOpenChange, interactions, data, labelId, descriptionId, resolve]);
}

type ContextType = (ReturnType<typeof useDialog> & {
    setLabelId: StateUpdater<string | undefined>;
    setDescriptionId: StateUpdater<string | undefined>;
}) | null;

const DialogContext = createContext<ContextType>(null);

export const useDialogContext = () => {
    const context = useContext(DialogContext);

    if (context == null) {
        throw new Error("Dialog components must be wrapped in <Dialog />");
    }

    return context;
};

export function Dialog({children, ...options}: {children: ComponentChildren} & DialogOptions) {
    const dialog = useDialog(options);
    return <DialogContext.Provider value={dialog}>{children}</DialogContext.Provider>;
}

const DialogOverlay = styled(FloatingOverlay)`
    background: rgba(0, 0, 0, .5);
    backdrop-filter: blur(1rem);
    display: grid;
    place-items: center;
    
    > [role=dialog] {
        box-shadow: 0 0 1em rgba(0, 0, 0, .5);
    }
`;

const _DialogContent = styled.div`
    > * {
        max-height: 90vh;
        display: flex;
        flex-direction: column;
    }
`;

export const DialogContent = forwardRef<HTMLElement, HTMLProps<HTMLElement>>(
    function DialogContent(props, propRef) {
        const { context: floatingContext, ...context } = useDialogContext();
        const ref = useMergeRefs([context.refs.setFloating as unknown as Ref<HTMLElement>, propRef as Ref<HTMLElement>]);

        if (!floatingContext.open) return null;

        return <FloatingPortal>
            <DialogOverlay lockScroll>
                <FloatingFocusManager context={floatingContext}>
                    <_DialogContent
                        ref={ref}
                        aria-labelledby={context.labelId}
                        aria-describedby={context.descriptionId}
                        {...context.getFloatingProps(props)}
                    >
                        {props.children}
                    </_DialogContent>
                </FloatingFocusManager>
            </DialogOverlay>
        </FloatingPortal>;
    }
);

export const DialogClose = forwardRef<HTMLButtonElement, ButtonProps & {reason?: string}>(
    ({reason, ...props}, ref) => {
        const { setOpen, resolve } = useDialogContext();
        return <Button type="button" {...props} ref={ref} onClick={() => {
            setOpen(false);
            if (resolve && reason) {
                resolve(reason);
            }
        }} />;
    }
);
